import { useState, useEffect } from 'react';
import { useTenantContext } from '../context/TenantContext';
import { getStudentProfileByToken, getStudentEnrollments } from '../services/elearningStudentService';

/**
 * Hook pour gérer les inscriptions d'un étudiant
 */
export interface EnrollmentInfo {
  id: string;
  course_id: string;
  status: 'active' | 'completed' | 'suspended';
  progress_percentage: number;
}

export const useStudentEnrollments = () => {
  const { tenant } = useTenantContext();
  const [enrollments, setEnrollments] = useState<any[]>([]);
  const [enrollmentMap, setEnrollmentMap] = useState<Record<string, EnrollmentInfo>>({});
  const [enrolledCourseIds, setEnrolledCourseIds] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [studentProfile, setStudentProfile] = useState<any>(null);

  useEffect(() => {
    const loadEnrollments = async () => {
      setIsLoading(true);
      try {
        // Vérifier si l'utilisateur est connecté via le système d'authentification e-learning
        const token = localStorage.getItem('elearning_student_token');
        console.log('Token trouvé:', !!token);
        if (token && tenant?.id) {
          const profile = await getStudentProfileByToken(token);
          console.log('Profil étudiant:', profile ? { id: profile.id, email: profile.email } : 'Non trouvé');
          if (profile) {
            setStudentProfile(profile);
            // Utiliser une valeur par défaut vide si tenant.id est undefined
            const tenantId: string = tenant?.id || '';
            const profileId: string = profile?.id || '';
            console.log('Chargement des inscriptions pour:', { tenantId, profileId });
            const enrollmentsData = await getStudentEnrollments(profileId, tenantId);
            setEnrollments(enrollmentsData);
            
            // Extraire les IDs des cours auxquels l'utilisateur est inscrit
            const courseIds = enrollmentsData.map((enrollment: any) => enrollment.course_id);
            setEnrolledCourseIds(courseIds.filter(Boolean));
            
            // Créer une carte des inscriptions pour un accès rapide
            const enrollmentMapData: Record<string, EnrollmentInfo> = {};
            enrollmentsData.forEach((enrollment: any) => {
              if (enrollment.course_id) {
                enrollmentMapData[enrollment.course_id] = {
                  id: enrollment.id,
                  course_id: enrollment.course_id,
                  status: enrollment.status,
                  progress_percentage: enrollment.progress_percentage || 0
                };
              }
            });
            setEnrollmentMap(enrollmentMapData);
          }
        }
      } catch (error) {
        console.error("Erreur lors du chargement des inscriptions:", error);
      } finally {
        setIsLoading(false);
      }
    };

    loadEnrollments();
  }, [tenant?.id]);

  /**
   * Vérifie si l'utilisateur est inscrit à un cours spécifique
   */
  const isEnrolledInCourse = (courseId: string): boolean => {
    return enrolledCourseIds.includes(courseId);
  };

  /**
   * Obtient les informations d'inscription pour un cours spécifique
   */
  const getEnrollmentInfo = (courseId: string): EnrollmentInfo | null => {
    return enrollmentMap[courseId] || null;
  };

  /**
   * Détermine le statut d'affichage et l'action pour un cours inscrit
   */
  const getCourseAction = (courseId: string): { label: string; action: string; color: string } => {
    const enrollment = enrollmentMap[courseId];
    if (!enrollment) {
      return { label: "S'inscrire", action: "enroll", color: "bg-red-600 hover:bg-red-700" };
    }

    if (enrollment.status === 'completed') {
      return { label: "Terminé", action: "view", color: "bg-green-600 hover:bg-green-700" };
    } else if (enrollment.progress_percentage > 0) {
      return { label: "Continuer", action: "continue", color: "bg-blue-600 hover:bg-blue-700" };
    } else {
      return { label: "Commencer", action: "start", color: "bg-red-600 hover:bg-red-700" };
    }
  };

  return {
    enrollments,
    isLoading,
    studentProfile,
    isEnrolledInCourse,
    getEnrollmentInfo,
    getCourseAction,
    enrollmentMap
  };
};
